import { FC, ReactElement, useEffect, useMemo, useState } from "react"
import { useUsercommContextBLE } from "../../usercomm/local/ble/usercommProviderBLE"
import { FlexCol, FlexRow } from "./common"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowsAlt,
  faArrowsAltV,
  faArrowsUpDown,
  faDroplet,
  faL,
  faLocationDot,
  faSatellite,
  faSatelliteDish,
  faTemperatureHalf,
  faThermometer,
} from "@fortawesome/free-solid-svg-icons"
import {
  COLOR_BG_BLUE,
  COLOR_BG_DARK,
  COLOR_BG_GRAY,
  COLOR_BG_GREEN,
  COLOR_BG_LIGHT_BLUE,
  COLOR_BG_RED,
  colorHexToRgba,
} from "../../utils/utils"
import { CaretLeftFilled, ColumnHeightOutlined } from "@ant-design/icons"
import { Modal } from "antd"
import { Link } from "react-router-dom"
import { Label } from "./input"
import { Translated } from "../../utils/translated"
import { BatteryLevelWidget } from "./deviceConnectionWidgetBLE"

const DeviceSensorModal_Lidar: FC<{
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}> = ({ isOpen, setIsOpen }) => {
  const { stationSensors } = useUsercommContextBLE()

  const memoValueStr = useMemo(() => {
    if (stationSensors === null) {
      return "N/A"
    }
    return stationSensors.lidar_distance.toFixed(0) + " cm"
  }, [stationSensors?.lidar_distance])

  return (
    <Modal
      title={
        <div style={{ fontSize: "2rem" }}>
          <Label>
            <Translated keyEn="Lidar Distance" />
          </Label>
        </div>
      }
      width={"90vw"}
      open={isOpen}
      onOk={() => setIsOpen(false)}
      onCancel={() => setIsOpen(false)}
      footer={null}
    >
      <FlexRow
        style={{
          gap: "6rem",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <div style={{ fontSize: "14rem" }}>
          {
            <ColumnHeightOutlined
              style={{
                fontSize: "14rem",
              }}
            />
          }
        </div>
        <div
          style={{
            fontSize: "8rem",
          }}
        >
          {memoValueStr}
        </div>
      </FlexRow>
    </Modal>
  )
}

export const DeviceSensorsWidgetBLE: FC = () => {
  const { stationSensors, lugsafysState } = useUsercommContextBLE()
  // Compute all display strings in a single memo — avoids 5 separate setState calls
  // (and thus 5 re-renders) per BLE sensor heartbeat.
  const memoSensorValues = useMemo(() => {
    if (stationSensors === null) {
      return { temperature: "-", humidity: "-", lidar: "-", gnssAvail: false, gnss: "-" }
    }
    const gnssAvail = !(
      (stationSensors.gnss_lon === undefined || stationSensors.gnss_lon === 0) &&
      (stationSensors.gnss_lat === undefined || stationSensors.gnss_lat === 0)
    )
    return {
      temperature: stationSensors.temperature.toFixed(0),
      humidity: stationSensors.relative_humidity.toFixed(0),
      lidar: stationSensors.lidar_distance.toFixed(0),
      gnssAvail,
      gnss: gnssAvail
        ? `${stationSensors.gnss_lat.toFixed(1)}/${stationSensors.gnss_lon.toFixed(1)}`
        : "N/C",
    }
  }, [stationSensors])

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false)

  useEffect(() => {
    if (lugsafysState === null) {
      return
    }
    console.debug("Lugsafys State updated:", lugsafysState.toObject())
  }, [lugsafysState])

  const iconFontSize = "1.5rem"
  const subscriptFontSize = "0.8rem"

  const memoLugsafysWidget: ReactElement | null = useMemo(() => {
    if (lugsafysState === null || lugsafysState.is_supported === false) {
      return null
    }
    if (lugsafysState.is_connected === false) {
      return (
        <FlexCol
          style={{
            gap: 2,
            minWidth: "2rem",
            alignItems: "center",
          }}
        >
          <FontAwesomeIcon icon={faL} fontSize={iconFontSize} color={COLOR_BG_RED} />
          <span style={{ fontSize: subscriptFontSize }}>N/C</span>
        </FlexCol>
      )
    } else {
      const loadStr = lugsafysState.load_kg.toFixed(1)
      return (
        <FlexCol
          style={{
            gap: 2,
            minWidth: "2rem",
            alignItems: "center",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
            }}
          >
            <BatteryLevelWidget batteryLevel={lugsafysState.battery_level_perc} />
          </div>
          <FontAwesomeIcon icon={faL} fontSize={iconFontSize} color={COLOR_BG_GREEN} />
          <span style={{ fontSize: subscriptFontSize }}>{loadStr}&nbsp;kg</span>
        </FlexCol>
      )
    }
  }, [lugsafysState])

  let widgetWidth = 105
  if (!isCollapsed) {
    widgetWidth = 250
    if (memoLugsafysWidget !== null) {
      widgetWidth += 35
    }
  }

  return (
    <>
      <DeviceSensorModal_Lidar isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
      <div
        style={{
          width: widgetWidth,
          paddingLeft: 25,
          paddingTop: 7,
          paddingBottom: 7,
          backgroundColor: colorHexToRgba(COLOR_BG_LIGHT_BLUE, 0.2),
          borderRadius: 50,
          transition: "width 0.3s",
        }}
      >
        <div
          style={{
            position: "relative",
          }}
        >
          <FlexRow
            style={{
              justifyContent: "start",
              overflow: "hidden",
              width: "calc(100% - 45px)",
            }}
          >
            <div
              style={{ cursor: "pointer" }}
              onClick={() => {
                setIsModalOpen(true)
              }}
            >
              <FlexCol
                style={{
                  gap: 2,
                  minWidth: "2rem",
                  alignItems: "center",
                }}
              >
                <ColumnHeightOutlined
                  style={{
                    fontSize: iconFontSize,
                    color: COLOR_BG_DARK,
                  }}
                />
                <span style={{ fontSize: subscriptFontSize }}>{memoSensorValues.lidar}cm</span>
              </FlexCol>
            </div>
            <FlexCol
              style={{
                gap: 2,
                minWidth: "2rem",
                alignItems: "center",
              }}
            >
              <Link to="/settings/device#gnss">
                <FontAwesomeIcon
                  icon={faSatelliteDish}
                  fontSize={iconFontSize}
                  color={memoSensorValues.gnssAvail ? COLOR_BG_GREEN : COLOR_BG_GRAY}
                />
              </Link>
              <span style={{ fontSize: subscriptFontSize }}>{memoSensorValues.gnss}</span>
            </FlexCol>
            <FlexCol
              style={{
                gap: 2,
                minWidth: "2rem",
                alignItems: "center",
              }}
            >
              <FontAwesomeIcon
                icon={faTemperatureHalf}
                fontSize={iconFontSize}
                color={COLOR_BG_GREEN}
              />
              <span style={{ fontSize: subscriptFontSize }}>
                {memoSensorValues.temperature}&#8451;
              </span>
            </FlexCol>
            <FlexCol
              style={{
                gap: 2,
                minWidth: "2rem",
                alignItems: "center",
              }}
            >
              <FontAwesomeIcon icon={faDroplet} fontSize={iconFontSize} color={COLOR_BG_BLUE} />
              <span style={{ fontSize: subscriptFontSize }}>{memoSensorValues.humidity}%</span>
            </FlexCol>
            {memoLugsafysWidget}
          </FlexRow>
          <div
            style={{
              backgroundColor: colorHexToRgba(COLOR_BG_LIGHT_BLUE, 0.8),
              transform: isCollapsed ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.5s",
              position: "absolute",
              right: 0,
              top: -2,
              cursor: "pointer",
              marginRight: 5,
              borderRadius: "100%",
              width: 45,
              height: 45,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            onClick={() => {
              setIsCollapsed(!isCollapsed)
            }}
          >
            <CaretLeftFilled
              style={{
                fontSize: "2rem",
                color: COLOR_BG_BLUE,
              }}
            />
          </div>
        </div>
      </div>
    </>
  )
}
